/* eslint-disable */
import { getLocale, experimentalStaticLocale } from '../runtime.js';

/** @typedef {import('../runtime.js').LocalizedString} LocalizedString */

/** @typedef {{}} Guide_Docs_MdInputs */

const en_guide_docs_md = /** @type {(inputs: Guide_Docs_MdInputs) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`## 7. Use any AI assistant (a guide for agents)

Bystep publishes a document written **for AI assistants**, not for people: [agent-guide.en.md](https://bystep.dev/agent-guide.en.md) (English) and [agent-guide.md](https://bystep.dev/agent-guide.md) (Indonesian). It explains how to tell whether the user is a programmer, which door to suggest, the exact button names, and the honest limits. An assistant that reads it will not try to run \`npx @bystep/cli\` inside a chat.

If you are not a programmer, just paste this sentence into ChatGPT, Claude or Gemini:

\`\`\`
Read https://bystep.dev/agent-guide.en.md first, then guide me step by step. I'm not a programmer.
\`\`\`

For agents with a terminal (Claude Code, Codex, Cursor) and integrators:

- [llms.txt](https://bystep.dev/llms.txt) — the documentation index in the standard LLM format; [llms-full.txt](https://bystep.dev/llms-full.txt) is the single-file version.
- [/api/schema](https://bystep.dev/api/schema) — JSON: every CLI command, MCP tool (stdio & hosted), and the JSON Schema of the Task Packet and evidence payload.
- \`npx @bystep/cli --llms\` or \`npx @bystep/cli task --llms\` — a JSON manifest built from the installed CLI itself, so agents read the exact arguments instead of guessing.

### Playbooks

[/playbooks](https://bystep.dev/playbooks) lists ready-made app recipes (shop, tutoring, notary, cooperative, clinic, foundation) plus community playbooks. To share yours: put a \`bystep-playbook.json\` at the root of a public GitHub project and add the topic \`bystep-playbook\`. Fields: \`id\`, \`name\` (id/en), \`desc\` (id/en), \`kitId\` (a Bystep kit id), \`examples\` (id/en lists) (max 3), \`rules[]\` (\`kind\` always|never|ask, \`templateId\`, \`text\` (id/en)), \`tags[]\`. Public list: \`GET /api/public/playbooks\`.
`)
};

const id_guide_docs_md = /** @type {(inputs: Guide_Docs_MdInputs) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`## 7. Pakai asisten AI mana pun (panduan untuk agen)

Bystep menyediakan dokumen yang ditulis **untuk asisten AI**, bukan untuk manusia: [agent-guide.md](https://bystep.dev/agent-guide.md) (Indonesia) dan [agent-guide.en.md](https://bystep.dev/agent-guide.en.md) (Inggris). Isinya: cara mengenali apakah pengguna itu programmer atau bukan, jalur mana yang harus disarankan, nama tombol yang benar, dan batasan yang jujur. Asisten yang membacanya tidak akan mencoba menjalankan \`npx @bystep/cli\` di dalam chat.

Kalau kamu bukan programmer, cukup salin kalimat ini ke ChatGPT, Claude, atau Gemini:

\`\`\`
Baca https://bystep.dev/agent-guide.md dulu, lalu pandu saya langkah demi langkah. Saya bukan programmer.
\`\`\`

Untuk agen yang punya terminal (Claude Code, Codex, Cursor) dan integrator:

- [llms.txt](https://bystep.dev/llms.txt) — indeks dokumentasi dalam format standar untuk LLM; [llms-full.txt](https://bystep.dev/llms-full.txt) versi lengkap satu berkas.
- [/api/schema](https://bystep.dev/api/schema) — JSON: semua perintah CLI, alat MCP (stdio & hosted), JSON Schema Task Packet dan bukti (evidence).
- \`npx @bystep/cli --llms\` atau \`npx @bystep/cli task --llms\` — manifest JSON perintah yang dibangun dari CLI yang terpasang, jadi agen membaca argumen yang persis, bukan menebak.

### Playbook

[/playbooks](https://bystep.dev/playbooks) berisi resep aplikasi siap pakai (warung, bimbel, notaris, koperasi, klinik, yayasan) plus playbook komunitas. Untuk membagikan milikmu: taruh \`bystep-playbook.json\` di akar proyek GitHub publik dan beri topik \`bystep-playbook\`. Kolom: \`id\`, \`name\` (id/en), \`desc\` (id/en), \`kitId\` (id kit Bystep), \`examples\` (daftar id/en) (maks 3), \`rules[]\` (\`kind\` always|never|ask, \`templateId\`, \`text\` (id/en)), \`tags[]\`. Daftar publik: \`GET /api/public/playbooks\`.
`)
};

/**
* | output |
* | --- |
* | "## 7. Use any AI assistant (a guide for agents) Bystep publishes a document written **for AI assistants**, not for people: [agent-guide.en.md](https://bystep..." |
*
* @param {Guide_Docs_MdInputs} inputs
* @param {{ locale?: "en" | "id" }} options
* @returns {LocalizedString}
*/
export const guide_docs_md = /** @type {((inputs?: Guide_Docs_MdInputs, options?: { locale?: "en" | "id" }) => LocalizedString) & import('../runtime.js').MessageMetadata<Guide_Docs_MdInputs, { locale?: "en" | "id" }, {}>} */ ((inputs = {}, options = {}) => {
	const locale = experimentalStaticLocale ?? options.locale ?? getLocale()
	if (locale === "id") return id_guide_docs_md(inputs)
	return en_guide_docs_md(inputs)
});